import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const api = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

function getToken(): string | null {
  try {
    const stored = localStorage.getItem('rotkreuz-auth');
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.state?.token ?? null;
    }
  } catch {
    // silently fail
  }
  return null;
}

// Request interceptor: attach JWT
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

// Response interceptor: handle 401
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('rotkreuz-auth');
      // Login-Seite nicht neu laden (sonst geht die Fehlermeldung verloren);
      // ansonsten das aktuelle Ziel merken, damit Deep-Links erhalten bleiben.
      if (!/^\/login\/?$/.test(window.location.pathname)) {
        const returnTo = window.location.pathname + window.location.search;
        window.location.href = '/login?returnTo=' + encodeURIComponent(returnTo);
      }
    }
    return Promise.reject(error);
  },
);

export interface ApiError {
  message: string;
  status: number;
  details?: Record<string, string[]>;
}

export function extractError(error: unknown): ApiError {
  if (error instanceof AxiosError && error.response) {
    const data = error.response.data as Record<string, unknown> | undefined;
    return {
      message: (data?.message as string) ?? error.message,
      status: error.response.status,
      details: data?.details as Record<string, string[]> | undefined,
    };
  }
  if (error instanceof Error) {
    return { message: error.message, status: 0 };
  }
  return { message: 'Ein unbekannter Fehler ist aufgetreten', status: 0 };
}

export default api;
